import { z } from "zod";

import { isAllowedWildcard } from "../recipient-domains";
import { DEFAULT_ROOM_TYPE, ROOM_TYPES } from "../room-path";

import { widgetSchema } from "./widgets";

// Room + Page schema. This is migration contract #1 — the JSON shape
// survives v0.5 → v1 unchanged (Postgres jsonb → MSSQL nvarchar(max),
// storage type swap, schema unchanged).
//
// At v0.5 these JSON blobs live in the room_pages table on Vercel Postgres.
// Provisioning (W4) writes one row per room_page; rendering (this file's
// consumers) reads them back through the page route.

// Templates correspond to React components in src/deal-room/templates/.
// Adding a template:
//   1. Add to this enum
//   2. Implement the component in templates/
//   3. Register it in templates/registry.ts
export const pageTemplateSchema = z.enum([
    "welcome",
    "capabilities_overview",
    "programs",
    "supplier_assortment_assessment",
    "your_opportunity",
    "commercial_proposal",
    "contracts",
    "map",
    "marketing",
    "onboarding",
    "uploads",
    // Common supplier questions, moved off Capabilities on 2026-09-04.
    "faq",
    // How a Bison partnership works, first child of About Us (2026-09-04).
    "how_we_partner",
    // The in-room Partnership Form replacing the Google Doc forms (2026-10-28, trial on heartland-furniture).
    "partnership_form",
    // Legacy templates kept for backwards-compat with older provisioned rooms.
    // New rooms should not use these - capabilities split into capabilities +
    // programs, KPIs replaced by uploads, white-label removed.
    "kpi_framework",
    "specialty",
    // Cold Lead pod additions (added when W1 Cold extension lands)
    "supplier_portal_preview",
    "case_studies",
    // Content Optimization room (2026-09-23, Eric): the proposal's four
    // proposal as one page with in-page anchors (Eric, 2026-09-23, replacing
    // four narrative pages the same day), the ASIN-by-service matrix, the one
    // Our capabilities page, and the checkout.
    "co_proposal",
    "co_assortment",
    "co_capabilities",
    "co_checkout",
]);

export type PageTemplate = z.infer<typeof pageTemplateSchema>;

export const pageSchema = z.object({
    slug: z.string().regex(/^[a-z0-9-]+$/),
    /** Display title shown on the page itself. */
    title: z.string(),
    /**
     * Short label shown in the room nav tab. Falls back to `title` when omitted.
     * Page titles tend to be sentence-case ("Welcome to your deal room"),
     * while nav tabs read better as nouns ("Home"). This separation lets
     * each surface phrase its own way.
     */
    navLabel: z.string().optional(),
    /**
     * Parent node in the left nav. Consecutive pages sharing a navGroup render
     * as children of one collapsible parent (e.g. "About Us", "Onboarding").
     * Set in canonical per template; stored rooms normally never carry it.
     */
    navGroup: z.string().optional(),
    template: pageTemplateSchema,
    widgets: z.array(widgetSchema),
    // Template-level data slot (for fields the template renders directly,
    // independent of widgets - e.g., supplier name in Welcome's hero).
    data: z.record(z.string(), z.unknown()).optional(),
    // Suppress this page from the room nav and from deep-link rendering.
    // Provisioning sets this based on the lifecycle gate (e.g. the Onboarding
    // page is hidden until the Pipedrive deal reaches the Contracting stage).
    // Bison staff bypass the hide so the page can still be previewed end-to-end.
    hidden: z.boolean().optional(),
});

export type Page = z.infer<typeof pageSchema>;

export const podTemplateSchema = z.enum(["warm_lead", "cold_lead", "white_label"]);

// The room types live in room-path.ts (ROOM_TYPES); this is their schema.
export const roomTypeSchema = z.enum(ROOM_TYPES);

export type RoomType = z.infer<typeof roomTypeSchema>;

// Content Optimization room: the services a supplier can buy per ASIN, in
// matrix column order. Each is a column on the assortment page and a key in
// the rate card. Copy is title, bullets, description and backend search terms
// as one service, its score their average; images precede infographics
// (Eric, 2026-09-24; six separate columns until then).
export const CO_SERVICES = ["copy", "images", "infographics", "aplus"] as const;
export const coServiceSchema = z.enum(CO_SERVICES);
export type CoService = z.infer<typeof coServiceSchema>;

// A dimension score is 0-100 from the listing-audit rubric. `null` is TBD:
// the element is present but has not been evaluated yet (infographics need
// the deep dive's vision pass). A MISSING element is 0, never null, so the
// hygiene slider offers it (Eric, 2026-09-23).
export const coScoreSchema = z.number().min(0).max(100).nullable();

export const coAssortmentRowSchema = z.object({
    asin: z.string(),
    sku: z.string().optional(),
    title: z.string(),
    // Trailing-12-month Amazon revenue from bison-office-db; null when no
    // order line could be attributed to the listing.
    revenue12mo: z.number().nullable(),
    // Trailing-12-month Amazon orders behind revenue12mo (2026-10-05, Eric).
    orders12mo: z.number().nullable().optional(),
    // Every seller's Amazon sales on the listing, last 30 days annualized
    // (supplier-listing-proposal market.py, an estimate); a variation family's
    // figure is split across its listings. Absent on rooms provisioned before 2026-10-05.
    market12mo: z.number().nullable().optional(),
    // On a listing in a variation family of two or more: the family's listing count and
    // all-seller total, which market12mo is this listing's share of (2026-10-05, Eric).
    marketFamily: z.object({ listings: z.number(), sales: z.number() }).nullable().optional(),
    hygiene: coScoreSchema,
    scores: z.record(coServiceSchema, coScoreSchema),
});
export type CoAssortmentRow = z.infer<typeof coAssortmentRowSchema>;

export const contentOptimizationSchema = z.object({
    // Per-ASIN price of each service for this room, set at provision.
    rates: z.record(coServiceSchema, z.number().nonnegative()),
    assortment: z.array(coAssortmentRowSchema),
    // When the scores and revenue were snapshotted (ISO 8601).
    scoredAt: z.string().optional(),
});
export type ContentOptimization = z.infer<typeof contentOptimizationSchema>;

export type PodTemplate = z.infer<typeof podTemplateSchema>;

export const supplierContextSchema = z.object({
    supplierId: z.string(), // BisonOffice Vendors.VendorId or external prospect id
    supplierName: z.string(),
    // Optional supplier wordmark / logo URL. When present, the room nav renders it
    // on the right side. When absent, supplierName is shown as text instead.
    // Accepts absolute URLs or root-relative paths (e.g. /supplier-logo.jpg).
    logoUrl: z.string().min(1).optional(),
    asins: z.array(z.string()).optional(),
    acquisitionModel: z
        .enum([
            "exclusive_partner",
            "hard_map_supplier",
            "few_competitors",
            "many_competitors",
            "amazon_1p_to_3p",
            "net_new_to_marketplace",
            "distressed_turnaround",
            "internal_3p_to_external",
            "non_core_marketplaces",
            "web_only_play",
            "white_label_only",
        ])
        .optional(),
});

export type SupplierContext = z.infer<typeof supplierContextSchema>;

// Shared shape for a displayed Bison contact card (primary SEM and the
// secondary onboarding manager use the same fields). `photoUrl` accepts an
// absolute URL (Contentful CDN headshots) OR a root-relative public-asset
// path (e.g. /crown-mark-logo.jpg) — same posture as supplier.logoUrl.
const bisonContactSchema = z.object({
    name: z.string(),
    role: z.string().optional(),
    // The rail card's header, e.g. "Your account manager" (exclusivity room, Elijah
    // 2026-10-27). Falls back to the template's default for the card.
    cardLabel: z.string().optional(),
    email: z.string().email().optional(),
    // Phone is shown as plain text + tel: link. No formatting enforced —
    // pass it the way you want it displayed (e.g. "+1 (555) 123-4567").
    phone: z.string().optional(),
    linkedinUrl: z.string().url().optional(),
    calendlyUrl: z.string().url().optional(),
    photoUrl: z.string().min(1).optional(),
});

export const roomSchema = z.object({
    slug: z.string().regex(/^[a-z0-9-]+$/),
    // Which kind of room this is; it picks the URL's first segment
    // (room-path.ts ROOM_TYPE_PREFIX) and the label a reader sees. Access,
    // recipients and invites are the same for every type. Rooms stored
    // before 2026-09-24 carry no value and read as deal rooms.
    roomType: z.enum(ROOM_TYPES).default(DEFAULT_ROOM_TYPE),
    // Present on a content_optimization room: the rate card and the scored
    // assortment the matrix renders. Snapshotted at provision (B5).
    contentOptimization: contentOptimizationSchema.optional(),
    podTemplate: podTemplateSchema,
    supplier: supplierContextSchema,
    pages: z.array(pageSchema).min(1),
    // Display config for the assigned Bison contact (Sr. Director / SEM).
    bisonContact: bisonContactSchema.optional(),
    // Optional secondary contact — the supplier onboarding manager shown
    // below the primary on the Welcome page. When absent, the renderer falls
    // back to DEFAULT_BISON_SECONDARY_CONTACT (Elijah Chang). Set by the
    // provisioning / update skills to override per-deal.
    bisonSecondaryContact: bisonContactSchema.optional(),
    // Authorised viewers. Magic-link sign-in checks an incoming email against
    // this list (case-insensitive). Bison staff (`@bisoncommerce.com`) bypass
    // the check so SMs/CEO can preview any room.
    //
    // Empty list = no external recipients yet (Bison staff still get in).
    // An entry may also be a domain wildcard ("*@hushcrib.com") admitting every
    // address at the supplier's own domain; public mailbox domains are refused
    // (see recipient-domains.ts).
    recipients: z
        .array(
            z.object({
                email: z.union([
                    z.string().email(),
                    z
                        .string()
                        .refine(isAllowedWildcard, {
                            message: "wildcard recipient must be *@<company-domain>, not a public email provider",
                        }),
                ]),
                name: z.string().optional(),
                role: z.string().optional(),
            }),
        )
        .default([]),
    // The supplier's signer, picked by staff in the access panel. Agreements
    // sent for signature default to this person; absent, the first named
    // non-Bison recipient (recipient-domains.ts roomSigner).
    signerEmail: z.string().email().optional(),
    // Provisioning + audit metadata.
    createdAt: z.string(), // ISO 8601
    updatedAt: z.string(),
});

export type Room = z.infer<typeof roomSchema>;
